import * as S from "sanctuary";
import { createSelector } from "reselect";

import { CB_ADMIN } from "@/sanctuary/typedefs";
import { DEFAULT_TIMEZONE } from "@/util/dates";

const INITIAL_STATE = {
  permissions: S.Just([]),
  agencies: S.Nothing,
  role: S.Nothing,
  id: S.Nothing,
  client: S.Nothing,
  isActive: S.Just(false),
  email: S.Nothing,
  requestError: S.Nothing,
  requestPending: false,
  firstName: S.Nothing,
  lastName: S.Nothing,
  timezone: DEFAULT_TIMEZONE,
  credentialsProvider: S.Nothing,
  editMyselfError: S.Nothing,
  editMyselfPending: false,
};

export const REQUEST_CURRENT_USER = "currentUser/REQUEST_CURRENT_USER";
export const requestCurrentUser = ({ accessToken, oktaAuth }) => {
  return {
    type: REQUEST_CURRENT_USER,
    payload: { accessToken, oktaAuth },
  };
};

export const REQUEST_CURRENT_USER_STARTED =
  "currentUser/REQUEST_CURRENT_USER_STARTED";
export const requestCurrentUserStarted = () => ({
  type: REQUEST_CURRENT_USER_STARTED,
});

export const SET_CURRENT_USER = "currentUser/SET_CURRENT_USER";
export const setCurrentUser = ({ user }) => ({
  type: SET_CURRENT_USER,
  payload: {
    user,
  },
});

export const SET_CURRENT_USER_ERROR = "currentUser/SET_CURRENT_USER_ERROR";
export const setCurrentUserError = ({ error }) => ({
  type: SET_CURRENT_USER_ERROR,
  payload: error,
  error: true,
});

export const EDIT_MYSELF = "currentUser/EDIT_MYSELF";
export const editMyself = ({ firstName, lastName, timezone, oktaAuth }) => ({
  type: EDIT_MYSELF,
  payload: { firstName, lastName, timezone, oktaAuth },
});

export const EDIT_MYSELF_SUCCEEDED = "currentUser/EDIT_MYSELF_SUCCEEDED";
export const editMyselfSucceeded = ({ profile }) => ({
  type: EDIT_MYSELF_SUCCEEDED,
  payload: { profile },
});

export const EDIT_MYSELF_FAILED = "currentUser/EDIT_MYSELF_FAILED";
export const editMyselfFailed = ({ error }) => ({
  type: EDIT_MYSELF_FAILED,
  payload: error,
  error: true,
});

export const REQUEST_CLIENT = "currentUser/REQUEST_CLIENT";
export const requestClient = ({ client, oktaAuth }) => ({
  type: REQUEST_CLIENT,
  payload: { client, oktaAuth },
});

export const REMOVE_CLIENT = "currentUser/REMOVE_CLIENT";
export const removeClient = () => ({
  type: REMOVE_CLIENT,
});

export const SET_OKTA_CREDENTIALS_PROVIDER =
  "currentUser/SET_OKTA_CREDENTIALS_PROVIDER";
export const setOktaCredentialsProvider = (credentialProvider) => ({
  type: SET_OKTA_CREDENTIALS_PROVIDER,
  payload: {
    credentialProvider,
  },
});

const reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case REQUEST_CURRENT_USER_STARTED:
      return {
        ...state,
        requestPending: true,
      };
    case SET_CURRENT_USER:
      return {
        ...state,
        ...payload.user,
        requestPending: false,
      };
    case SET_CURRENT_USER_ERROR:
      return {
        ...state,
        requestError: S.Just(payload),
        requestPending: false,
      };
    case EDIT_MYSELF:
      return {
        ...state,
        editMyselfPending: true,
      };
    case EDIT_MYSELF_SUCCEEDED:
      return {
        ...state,
        editMyselfPending: false,
        editMyselfError: S.Nothing,
        firstName: { value: payload.profile.first_name },
        lastName: { value: payload.profile.last_name },
      };
    case EDIT_MYSELF_FAILED:
      return {
        ...state,
        editMyselfPending: false,
        editMyselfError: S.Just(payload),
      };
    case REMOVE_CLIENT:
      return {
        ...state,
        client: S.Nothing,
      };
    case SET_OKTA_CREDENTIALS_PROVIDER:
      return {
        ...state,
        credentialsProvider: S.Just(payload.credentialProvider),
      };
    default:
      return state;
  }
};

export const getState = (state) => state.currentUser;
export const userIsAuthorized = (state) =>
  S.fromMaybe(false)(getState(state).isActive);
export const getCurrentUserPermissions = (state) => getState(state).permissions;
export const getCurrentUserRole = (state) => getState(state).role;
export const getCurrentUserId = (state) => getState(state).id;
export const getCurrentUserAgencies = (state) => getState(state).agencies;
export const getCurrentUserClient = (state) => getState(state).client;
export const getCurrentUserEmail = (state) => getState(state).email;
export const getUserTimezone = (state) => {
  const timezone = getState(state).timezone;
  return Object.keys(timezone) > 0 ? timezone : S.Just(DEFAULT_TIMEZONE);
};
export const editMyselfIsPending = (state) => getState(state).editMyselfPending;
export const editMyselfError = (state) => getState(state).editMyselfError;

export const getCurrentUserClientBrand = createSelector(
  getCurrentUserClient,
  S.pipe([S.map(S.prop("brand")), S.fromMaybe("")])
);

export const getCurrentUserClientSlug = createSelector(
  getCurrentUserClient,
  S.pipe([S.map(S.prop("slug")), S.fromMaybe("")])
);

export const getClientIdentifier = createSelector(
  getCurrentUserClient,
  (clientIdentifier) => clientIdentifier?.value?.slug
);

export const getCurrentUserAgencyNames = createSelector(
  getCurrentUserAgencies,
  S.pipe([S.map(S.map(S.prop("name"))), S.fromMaybe([])])
);

export const currentUserIsCitybaseAdmin = createSelector(
  getCurrentUserRole,
  S.equals(S.Just(S.Right(CB_ADMIN)))
);

export default reducer;
